// @ts-strict-ignore
import type {
  Action,
  Attribute,
  PopulatedThreshold,
  ServiceAttributesById,
  ServiceObject,
  ServicesByName,
  ThresholdLeft,
  WorkflowInfo,
} from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { getActionName, getActionTypeText, isActionOfType } from 'flow/service/actions';
import type { NodeLineage } from '~/dashboard/views/workflows/service/graph';
import {
  getErrorAttributeLabel, isErrorAttribute, isJsonAttribute, serviceHasAttributes,
} from '~/dashboard/views/workflows/service/services';
import { getAggregationConfigForAttribute } from 'flow/store/engine/update/helpers';
import type { AggregationConfigByObjectType } from 'flow/editor/components/aggregations/types';
import { canLinkToNode } from 'flow/editor/components/Graph/nodes/BaseNode/NodeConnector/helpers';
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import type { SelectedOperator } from './OperatorWrapper';
import isEqual from 'lodash/isEqual';
import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import type { MultiSelectProps } from '@usealloy/component-library';
import { getActionBorderBottom } from 'flow/editor/styles';
import { checkIfServiceHasMLAAttributes } from '~/dashboard/views/workflows/service/mla';
import type { MLAType } from '~/dashboard/views/workflows/service/mla';
import type { RulesWithMLA } from 'flow/editor/components/mla/types';
import { getFirstEligibleActionForDynamicComparison } from 'flow/store/engine/update/thresholds';
import { isListInclusionOperator } from 'flow/store/engine/update/thresholdOperatorHelpers';
import { NODE_WIDTH, COLUMN_SPACING } from 'flow/service/graph';
import {
  invalidLeftSideDynamicComparisonTypes,
  supportedNullActionTypes,
} from 'flow/service/thresholds';
import {
  AGGREGATION_EDIT_OPTION_LABEL,
  COMMA_DELINEATOR,
  COMMA_DELINEATOR_DISPLAY,
} from './constants';

export function truncateLabel(label: string, lengthLimit: number) {
  return label.length >= lengthLimit ? `${label.substring(0, lengthLimit)}...` : label;
}

export const canDynamicallyCompareActions = (leftAction: Action, rightAction: Action) => {
  const violatingConditions = [
    isActionOfType(leftAction, ActionType.INPUT) && isActionOfType(rightAction, ActionType.OUTPUT),
    isActionOfType(leftAction, ActionType.OUTPUT) && isActionOfType(rightAction, ActionType.INPUT),
    isActionOfType(leftAction, ActionType.OUTPUT) && isActionOfType(rightAction, ActionType.OUTPUT),
    leftAction.service?.internalName === 'IdentityElementVelocity'
      && isActionOfType(rightAction, ActionType.OUTPUT),
    isActionOfType(rightAction, ActionType.OUTPUT), // we temporarily block output actions from being compared to anything
  ];
  if (violatingConditions.includes(true)) return false;
  return true;
};

export const buildAttributeDropdownOption = (
  attribute: Attribute,
  id?: string,
): DropdownOption => {
  const label = isErrorAttribute(attribute)
    ? getErrorAttributeLabel(attribute)
    : attribute.display_name;
  return {
    label,
    value: id || String(attribute.attribute_id),
    meta: {
      isMLA: attribute.is_multilevel,
      showAttributeWidget: isErrorAttribute(attribute),
      attribute,
    },
  };
};

export const buildAttributeOptions = ({
  service,
  workflow,
  RHSOptions,
  universalAttributes = {},
  allowMatchingAttributes = false,
  allowJsonAttributes = false,
}: {
  service: ServiceObject;
  workflow: WorkflowInfo;
  RHSOptions?: {
    leftDataType?: string;
    leftAction?: Action;
    rightAction?: Action;
  };
  universalAttributes?: ServiceAttributesById;
  allowMatchingAttributes?: boolean;
  allowJsonAttributes?: boolean;
}): DropdownOption[] => {
  const serviceAttributes = service.attributes ?? {};

  const allowedServiceAttributes: {
    [attribute_id: number]: Attribute;
  } = getAllowedAttributesForPortfolioWorkflow(workflow.type, service.name, serviceAttributes);

  const allowedAttributes: {
    [attribute_id: number]: Attribute;
  } = {
    ...allowedServiceAttributes,
    ...universalAttributes,
  };

  if (Object.keys(allowedAttributes).length === 0) return [];

  return Object.entries(allowedAttributes)
    .filter(([, attribute]) => {
      // Always filter out archived attributes
      if (attribute.archivedAt) return false;

      if (isJsonAttribute(attribute) && !allowJsonAttributes) return false;

      // Matching attributes are opt-in per call site, and then only for services where the
      // backend will actually compute them. PREVIEW-status ones stay hidden regardless.
      if (attribute.dataType === 'matching') {
        if (!allowMatchingAttributes) return false;
        if (!service.matching_attributes_enabled) return false;
        if (attribute.status === 'PREVIEW') return false;
      }

      // RHS-specific filtering when options are provided
      if (RHSOptions?.leftDataType && RHSOptions?.leftAction && RHSOptions?.rightAction) {
        const dataTypeNotMatchingLeftAttrDataType = attribute.dataType !== RHSOptions.leftDataType;
        if (
          dataTypeNotMatchingLeftAttrDataType
          || !canDynamicallyCompareActions(RHSOptions.leftAction, RHSOptions.rightAction)
        ) {
          return false;
        }
      }

      // Filter out MLA attributes without components
      const mlaWithoutComponents = attribute.is_multilevel && !attribute.components;
      return !mlaWithoutComponents;
    })
    .sort(([, a], [, b]) => a.display_name.localeCompare(b.display_name))
    .map(
      ([attributeId, attribute]): DropdownOption => (
        buildAttributeDropdownOption(attribute, attributeId)
      ),
    );
};

export const buildMLAAttributeOptions = ({
  baseOptions,
  currentAttribute,
}: {
  baseOptions: DropdownOption[];
  currentAttribute?: {
    attributeId: number;
    attribute: Attribute;
  };
}): DropdownOption[] => {
  const otherAttributes = currentAttribute
    ? baseOptions.filter(attribute => attribute.value !== String(currentAttribute.attributeId))
    : baseOptions;

  const mlaOptionArray: DropdownOption[] = [
    {
      label: 'Other Multi-Level Attributes',
      value: undefined,
      options: otherAttributes.filter(attribute => attribute.meta?.isMLA),
    },
    {
      label: 'All Other Attributes',
      value: undefined,
      options: baseOptions.filter(attribute => !attribute.meta?.isMLA),
    },
  ];

  // If no current attribute, just return the MLA structure
  if (!currentAttribute || !currentAttribute.attribute) {
    return mlaOptionArray;
  }

  const isAttributeNotArchived = !currentAttribute.attribute.archivedAt
    || currentAttribute.attribute.components?.length;
  const isCurrentAttrMLA = currentAttribute.attribute.isMultilevel;

  return !isCurrentAttrMLA
    ? mlaOptionArray
    : [
      isAttributeNotArchived && {
        label: 'Edit Attribute',
        value: String(currentAttribute.attributeId),
        meta: {
          shortenedLabel: currentAttribute.attribute.display_name,
        },
      },
      ...mlaOptionArray,
    ].filter(Boolean);
};

/**
 * Builds the attribute dropdown options when an aggregation is active:
 * "Edit Aggregation" at the top, and the remaining selectable objects grouped
 * under "Select a different object".
 *
 * "Select a different object" only lists objects whose active version is
 * selectable (active_selects === true, i.e. present in aggregationConfig), so
 * inactive objects (e.g. logins) can't be chosen as a new selection. The
 * currently-selected object always keeps its "Edit Aggregation" entry, even if it
 * is itself inactive (e.g. an existing rule).
 */
export const buildAggregationAttributeOptions = (
  options: DropdownOption[],
  displayName: string,
  attributeId: number,
  aggregationConfig: AggregationConfigByObjectType,
): DropdownOption[] => {
  const attributeIdStr = String(attributeId);

  // Exclude the current object (kept as "Edit Aggregation" below) and any object
  // whose active version isn't selectable.
  const otherOptions = options
    .filter(opt => opt.value !== attributeIdStr)
    .filter(opt => {
      const attributeName = opt.meta?.attribute?.attribute_name;
      return Boolean(attributeName)
        && Boolean(getAggregationConfigForAttribute(
          { attribute_name: attributeName, display_name: opt.label },
          aggregationConfig,
        ));
    });

  return [
    {
      label: AGGREGATION_EDIT_OPTION_LABEL,
      value: attributeIdStr,
      meta: { shortenedLabel: displayName, category: 'attribute' },
    },
    ...(otherOptions.length
      ? [
        {
          label: 'Select a different object',
          value: null,
          options: otherOptions,
        },
      ]
      : []),
  ];
};

// Convenience function that maintains backward compatibility
// while providing the new cleaner separation
export const buildAllAttributeOptions = ({
  service,
  workflow,
  RHSOptions,
  currentAttribute,
  aggregationDisplayName,
  universalAttributes,
  allowMatchingAttributes = false,
  aggregationConfig,
}: {
  service: ServiceObject;
  workflow: WorkflowInfo;
  RHSOptions?: {
    leftDataType?: string;
    leftAction?: Action;
    rightAction?: Action;
  };
  currentAttribute?: {
    attributeId: number;
    attribute: Attribute;
  };
  aggregationDisplayName?: string;
  universalAttributes?: ServiceAttributesById;
  allowMatchingAttributes?: boolean;
  aggregationConfig?: AggregationConfigByObjectType;
}): DropdownOption[] => {
  const baseOptions = buildAttributeOptions({
    service,
    workflow,
    RHSOptions,
    universalAttributes,
    allowMatchingAttributes,
  });

  if (aggregationDisplayName && currentAttribute) {
    return buildAggregationAttributeOptions(
      baseOptions,
      aggregationDisplayName,
      currentAttribute.attributeId,
      aggregationConfig,
    );
  }

  return checkIfServiceHasMLAAttributes(service.internal_name)
    ? buildMLAAttributeOptions({ baseOptions, currentAttribute })
    : baseOptions;
};

export const buildActionDropdownOption = ({
  action,
  id,
  lengthLimit,
}: {
  action: Action;
  id?: string;
  lengthLimit: number;
}): DropdownOption => {
  const actionName = getActionName(action);
  const labelPrefix = getActionTypeText(action);
  const nameWithColons = `${actionName}${action.type === 'service' ? ': ' : ''}`;
  return {
    label: `${labelPrefix}: ${actionName}`,
    value: id || action.publicId,
    meta: {
      shortenedLabel: truncateLabel(nameWithColons, lengthLimit),
    },
  };
};

const isActionEligible = (action: Action, services: ServicesByName): boolean => {
  if (!isActionOfType(action, ActionType.SERVICE)) return true;
  const service = services[action.service.internalName];
  return !!service && serviceHasAttributes(service);
};

export const buildActionOptions = ({
  actions,
  sourceAction,
  services,
  nodeLineage,
  lengthLimit,
  thresholdSide,
}: {
  actions: Record<string, Action>;
  sourceAction: Action;
  services: ServicesByName;
  nodeLineage: NodeLineage;
  lengthLimit: number;
  thresholdSide: Pick<ThresholdLeft, 'actionId'>;
}): DropdownOption[] => Object.entries(actions)
  .filter(([id, action]) => {
    // we can assume that thresholds will always be well formed.
    // returning early here enables the selected options in the 'dependents' tab to always be shown
    // otherwise, it can be filtered out if the selected option would cause a cycle within the focused action
    if (action.publicId === thresholdSide.actionId) return true;
    if (action.publicId === sourceAction.publicId) return false;
    if (isActionOfType(action, ActionType.OUTCOME)) return false;
    return canLinkToNode(nodeLineage, id) && isActionEligible(action, services);
  })
  .map(([id, action]) => buildActionDropdownOption({
    action,
    id,
    lengthLimit,
  }),
  )
  .sort((a, b) => a.label.localeCompare(b.label));

export function getSelectedOption({
  incomingValue,
  options,
}: {
  incomingValue: string;
  options: DropdownOption[];
}): DropdownOption | undefined {
  // First, try to find a direct match (but skip wrapper options with undefined value)
  const directMatch = options.find(item => item.value === incomingValue);
  if (directMatch) {
    return directMatch;
  }

  // If no direct match, recursively search in nested options
  for (const item of options) {
    if (item.options) {
      const nestedMatch = getSelectedOption({
        incomingValue,
        options: item.options,
      });
      if (nestedMatch) {
        return nestedMatch;
      }
    }
  }
  return undefined;
}

type OperatorOptionsParams = {
  actionType: ActionType;
  isDynamic: boolean;
  leftHasAttributeOptions: boolean;
  lhsDataType: string;
  supportsBestEffortParsing?: boolean;
};

export function getOperatorOptions(params: OperatorOptionsParams): DropdownOption[] {
  const { lhsDataType } = params;

  switch (lhsDataType) {
    case 'date':
    case 'timestamp':
      return buildDateOrTimestampOperatorOptions(params);
    case 'decimal':
    case 'integer':
      return buildNumericOperatorOptions(params);
    case 'string':
      return buildStringOperatorOptions(params);
    case 'reasoncodes':
      return buildReasonCodeOperatorOptions();
    case 'matching':
      return buildMatchingOperatorOptions();
    case 'boolean':
      return buildBooleanOperatorOptions();
    default: {
      return [];
    }
  }
}

function buildDateOrTimestampOperatorOptions({
  isDynamic,
}: OperatorOptionsParams): DropdownOption[] {
  const defaultOptions = [
    { label: '=', value: 'e' },
    { label: '!=', value: 'ne' },
    { label: '<', value: 'lt' },
    { label: '<=', value: 'le' },
    { label: '>', value: 'gt' },
    { label: '>=', value: 'ge' },
  ];

  return isDynamic
    ? [
      ...defaultOptions,
      { label: 'is in', value: 'includedin' },
      { label: 'is not in', value: 'notincludedin' },
    ]
    : defaultOptions;
}

function buildNumericOperatorOptions({
  actionType,
  isDynamic,
  supportsBestEffortParsing = false,
}: OperatorOptionsParams): DropdownOption[] {
  const defaultOptions = [
    { label: '=', value: 'e' },
    { label: '!=', value: 'ne' },
    { label: '<', value: 'lt' },
    { label: '<=', value: 'le' },
    { label: '>', value: 'gt' },
    { label: '>=', value: 'ge' },
    { label: 'is in', value: 'includedin' },
    { label: 'is not in', value: 'notincludedin' },
  ];

  if (isDynamic) return defaultOptions;

  const rangeOption = { label: 'in the range of', value: 'range' };
  const nullOptions = [
    { label: 'is null', value: 'e', meta: { RHS: null } }, // the existence of RHS suggests we need to dispatch to the RHS
    { label: 'is not null', value: 'ne', meta: { RHS: null } }, // the value that we dispatch to the RHS is the RHS's value
  ];

  return actionType === ActionType.INPUT
    || (supportsBestEffortParsing && supportedNullActionTypes.has(actionType))
    ? [...defaultOptions, rangeOption, ...nullOptions]
    : [...defaultOptions, rangeOption];
}

function buildStringOperatorOptions({
  actionType,
  isDynamic,
  leftHasAttributeOptions,
  supportsBestEffortParsing = false,
}: OperatorOptionsParams): DropdownOption[] {
  const defaultOptions = [
    { label: '=', value: 'e' },
    { label: '!=', value: 'ne' },
    { label: 'is matching (regex)', value: 'in' },
    { label: 'is not matching (regex)', value: 'nin' },
    { label: 'contains', value: 'con' },
    { label: 'does not contain', value: 'ncon' },
    { label: 'is in', value: 'includedin' },
    { label: 'is not in', value: 'notincludedin' },
  ];

  if (leftHasAttributeOptions) {
    return isDynamic ? [{ label: '=', value: 'e' }] : [];
  }

  if (supportsBestEffortParsing && supportedNullActionTypes.has(actionType) && !isDynamic) {
    return [
      ...defaultOptions,
      { label: 'is null', value: 'e', meta: { RHS: '' } },
      { label: 'is not null', value: 'ne', meta: { RHS: '' } },
    ];
  }

  return defaultOptions;
}

function buildReasonCodeOperatorOptions(): DropdownOption[] {
  return [
    { label: '(at least one) are contained in', value: 'in' },
    { label: 'are not contained in', value: 'nin' },
  ];
}

function buildMatchingOperatorOptions(): DropdownOption[] {
  return [
    { label: 'is matched', value: 'e' },
    { label: 'score >', value: 'gt' },
    { label: 'score >=', value: 'ge' },
    { label: 'score <', value: 'lt' },
    { label: 'score <=', value: 'le' },
  ];
}

function buildBooleanOperatorOptions(): DropdownOption[] {
  return [{ label: '=', value: 'e' }];
}

export function isSameOperatorOption(
  option: DropdownOption,
  selectedOperator: SelectedOperator,
): boolean {
  if (selectedOperator.meta || option.meta) {
    if (!isEqual(selectedOperator.meta, option.meta)) {
      return false;
    }
  }
  return selectedOperator.value === option.value;
}

export function getSelectedOperatorOption(
  selectedOperator: SelectedOperator,
  operatorOptions: DropdownOption[],
): DropdownOption {
  return operatorOptions.find(option => isSameOperatorOption(option, selectedOperator));
}

export function shouldRenderOperatorDropdown(
  populatedThreshold: PopulatedThreshold,
  lhsDataType: string | null,
  isDynamic: boolean,
): boolean {
  return (
    !isActionOfType(populatedThreshold.left.action, [ActionType.TAG, ActionType.OUTCOME_REASON])
    && (isDynamic
      || !isMultiSelectRHS({
        lhsDataType,
        leftAttribute: populatedThreshold.left.attribute,
      }))
    && (isDynamic || lhsDataType !== 'boolean')
    && lhsDataType !== null
    && populatedThreshold.operator !== null
  );
}

export function isMultiSelectRHS({
  lhsDataType,
  leftAttribute,
}: {
  lhsDataType: string;
  leftAttribute: Attribute;
}) {
  return lhsDataType === 'string' && !!leftAttribute?.values;
}

export function hasAttributeOptions(attribute: Attribute | null): boolean {
  if (!attribute || !attribute.values) return false;

  const options = attribute.values.split(',');
  return options.length > 0 && options[0] !== '';
}

export function isNumericDataType(dataType: string) {
  return ['integer', 'decimal'].includes(dataType);
}

export function getDropdownTextStyles(action: Action) {
  switch (action?.type) {
    case ActionType.SERVICE: {
      return { color: 'var(--green-green-550, #3A795F)' };
    }
    case ActionType.OUTPUT:
    case ActionType.INPUT:
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
    case ActionType.MATRIX_MODEL: {
      return { color: 'var(--cl-juicyBlue-500)' };
    }
    default:
      break;
  }

  return { color: 'var(--cl-text-default)' };
}

export function getMultiSelectProps({
  action,
  options,
}: {
  action?: Action;
  options: {
    isPreview: boolean;
    isCTAOptionAvailable?: boolean;
  };
}): Partial<MultiSelectProps> {
  const { isPreview } = options;

  return {
    ...MULTISELECT_PROPS,
    customStyleFunctions: {
      ...MULTISELECT_PROPS.customStyleFunctions,
      container: () => ({
        margin: '0',
        opacity: isPreview ? '0.5' : '1',
      }),
      singleValue: () => ({
        borderTop: '2px solid transparent',
        borderBottom: getActionBorderBottom(action),
        fontFamily: 'var(--cl-font-family-mono)',
        fontSize: 'var(--cl-font-size-2)',
        fontWeight: 'var(--cl-font-weight-bold)',
        ...getDropdownTextStyles(action),
      }),
      valueContainer: () => ({
        padding: '0 3px',
        caretColor: 'var(--cl-yellow-500)',
      }),
      menuList: () => ({}),
      menu: () => ({
        position: 'absolute',
        right: 'unset',
        borderRadius: 'var(--cl-radius-3)',
        minWidth: '250px',
        maxWidth: '600px',
        width: 'max-content',
        boxShadow: '0px 2px 8px 0px rgba(0,0,0, 0.12)',
        border: '1px solid var(--cl-gray-300)',
        overflow: 'hidden',
      }),
      multiValue: () => ({
        margin: '0 2px',
        height: '24px',
        alignItems: 'center',
      }),
    },
  };
}

export const getCommaDelineatedValues = (str: string) => str.split(COMMA_DELINEATOR);

export const getCommaDelineatedDisplayValues = (str: string) => str.split(COMMA_DELINEATOR_DISPLAY);

export const getCommaDelineatedString = (values: string[]) => values.join(COMMA_DELINEATOR);

export const getCommaDelineatedDisplayString = (values: string[]) => (
  values.join(COMMA_DELINEATOR_DISPLAY)
);

export const scrubTrailingSpaces = (commaDelineatedDisplayString: string) => (
  getCommaDelineatedString(getCommaDelineatedDisplayValues(commaDelineatedDisplayString))
);

export const addTrailingSpaces = (commaDelineatedString: string) => (
  getCommaDelineatedDisplayString(getCommaDelineatedValues(commaDelineatedString))
);

export const truncateLabelWithRemainder = (values: string[], lengthLimit: number) => {
  // scan through string values, remove values that go past our character limit
  let charCount = 0;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    const newLength = charCount + value.length + COMMA_DELINEATOR_DISPLAY.length;

    if (newLength >= lengthLimit) {
      // one big value case
      if (values.length === 1) {
        return truncateLabel(getCommaDelineatedDisplayString(values), lengthLimit);
      }
      // only include string values in label that are below the length limit
      const valuesToInclude = values.slice(0, i);

      if (valuesToInclude.length === 0) {
        return truncateLabel(getCommaDelineatedDisplayString(values), lengthLimit);
      }

      const remainder = values.length - valuesToInclude.length;
      const displayString = getCommaDelineatedDisplayString(valuesToInclude);

      return `${displayString}, ${remainder} more...`;
    }

    charCount = newLength;
  }
  return getCommaDelineatedDisplayString(values);
};

export const computeRuleItemLengthLimit = (containerWidth: number) => {
  // On tooltip first render we don't measure width; fall back to tooltip's max width
  const fallbackTooltipWidth = (NODE_WIDTH * 2) + COLUMN_SPACING; // matches BaseNode tooltip max
  const effectiveWidth = containerWidth > 1 ? containerWidth : fallbackTooltipWidth;
  const calculatedLimit = Math.floor(effectiveWidth / 10);
  return Math.min(calculatedLimit, 44);
};

export const getMLAFootnoteNumber = (
  rulesWithMLA: RulesWithMLA,
  populatedThreshold: PopulatedThreshold,
  type: (typeof MLAType)[keyof typeof MLAType],
): number => rulesWithMLA.findIndex(
  rule => rule.publicId === populatedThreshold.publicId && rule.type === type,
);

export const canSwitchToDynamicComparison = ({
  populatedThreshold,
  nodeLineage,
  actions,
  workflow,
  services,
  lhsDataType,
  universalAttributes,
}: {
  populatedThreshold: PopulatedThreshold;
  nodeLineage: NodeLineage;
  actions: Record<string, Action>;
  workflow: WorkflowInfo;
  services: ServicesByName;
  lhsDataType: string;
  universalAttributes?: ServiceAttributesById;
}) => {
  if (
    lhsDataType === 'reasoncodes'
    || lhsDataType === 'matching'
    || isListInclusionOperator(populatedThreshold.operator)
  ) {
    return false;
  }

  const actionIdsConnectedDownstreamFromThresholdOwner = new Set<string>([
    ...nodeLineage.directOutgoers,
    ...nodeLineage.extendedOutgoers,
    populatedThreshold.left.actionId,
  ]);

  const firstEligibleActionForDynamicComparison = getFirstEligibleActionForDynamicComparison({
    threshold: populatedThreshold,
    actions,
    workflow,
    services,
    actionIdsConnectedDownstreamFromThresholdOwner,
    universalAttributes,
  });

  return (
    !invalidLeftSideDynamicComparisonTypes.has(populatedThreshold.left.action.type)
    && !!firstEligibleActionForDynamicComparison
  );
};

export const getShouldHideTooltip = (
  selectedOption: DropdownOption | DropdownOption[] | undefined,
) => (selectedOption && !Array.isArray(selectedOption) && !selectedOption?.meta?.shortenedLabel)
  || !selectedOption
  || (Array.isArray(selectedOption) && !selectedOption.length);
